/**
 * Remove credential-like query parameters from a URL before it is recorded as
 * analytics (event_log page_location, GA4 page_location).
 *
 * WHY: page_view stored window.location.href verbatim, so Polar's success
 * redirect wrote a live `customer_session_token` (access to the buyer's Polar
 * customer portal) into event_log for the first Creator Pack sale (2026-10-17),
 * and the same URL went to GA4. Any token that rides on a URL would do the same.
 *
 * Marketing and funnel parameters (utm_*, gclid, installId, checkout_id, from,
 * plan, kind) are kept: attribution and the connect funnel read them.
 * Pure, no I/O; safe on the client and the server.
 */

/** Any parameter whose name contains one of these is a credential. */
const SENSITIVE_SUBSTRINGS = [
  'token',
  'secret',
  'password',
  'passwd',
  'session',
  'signature',
  'capability',
  'apikey',
  'api_key',
  'access_key',
  'private',
  '__clerk',
];

/** Short names that are credentials only as an exact match (OAuth code/state etc.). */
const SENSITIVE_EXACT = new Set(['code', 'state', 'key', 'sig', 'otp', 'ticket', 'auth', 'jwt']);

export const SCRUBBED = 'redacted';

export function isSensitiveParam(name: string): boolean {
  const lower = name.toLowerCase();

  return SENSITIVE_EXACT.has(lower) || SENSITIVE_SUBSTRINGS.some((s) => lower.includes(s));
}

function scrubParams(params: URLSearchParams): boolean {
  let changed = false;

  for (const name of [...new Set(params.keys())]) {
    if (isSensitiveParam(name)) {
      params.set(name, SCRUBBED);
      changed = true;
    }
  }

  return changed;
}

/**
 * The URL with every sensitive query (and hash-fragment) parameter's value
 * replaced by `redacted`. Keeps the parameter name so the funnel step is still
 * readable. Returns the input unchanged when it is not a parseable absolute URL.
 */
export function scrubSensitiveUrl(raw: string): string {
  let url: URL;

  try {
    url = new URL(raw);
  } catch {
    return raw;
  }

  const queryChanged = scrubParams(url.searchParams);

  // OAuth implicit flows put tokens in the fragment (#access_token=...).
  let hashChanged = false;

  if (url.hash.includes('=')) {
    const hashParams = new URLSearchParams(url.hash.slice(1));
    hashChanged = scrubParams(hashParams);

    if (hashChanged) url.hash = hashParams.toString();
  }

  return queryChanged || hashChanged ? url.toString() : raw;
}
